import { ApolloCache, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import {
  namedOperations,
  PimProductTypeRefInput,
} from "../../../clients/graphqlTypes.js";
import {
  ProductPreferenceFragment,
  useAddProductPreferencesMutation,
  useDeleteProductPreferencesMutation,
  useProductPreferencesQuery,
} from "./ProductPreferences.generated.js";
import {
  articleNumberIdentifierKey,
  indexPreferencesByArticle,
  NO_PREFERENCE,
  ProductPreferenceScopes,
} from "./productPreferences.js";

/**
 * Preferences rank search results server-side, so every cached search is stale once one
 * changes — including those for filters not currently on screen.
 */
function evictRankedProductSearches(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "pimSearchProducts" });
  cache.evict({ id: "ROOT_QUERY", fieldName: "pimSearchGroupedProducts" });
  cache.gc();
}

/**
 * The caller's preferences for one product type, indexed per article, plus the two writes.
 *
 * The product type is addressed by ref rather than by id, because a preference stored under a
 * superseded product type system has no id to address it by — only its keys.
 *
 * `preferredStateKnown` is deliberately separate from the preferred flags: until the query
 * resolves, an article's state is *unknown*, not *unpreferred*, and a control that renders the
 * unpreferred state first would flash every row and then flip.
 */
export function useProductPreferences(
  productType: PimProductTypeRefInput | null
) {
  const client = useApolloClient();

  const query = useProductPreferencesQuery({
    client,
    variables: { productType },
    skip: productType === null,
  });

  // `previousData` carries the list through a refetch. A write refetches this query, and
  // reverting to "unknown" in between would blank every control that reads it. Apollo keeps
  // it across a *variable* change too, though, and another product type's scopes would let a
  // control write against the type it is no longer showing — so it is reused only while the
  // product type is the one that data was last loaded for.
  const productTypeKey =
    productType === null ? null : JSON.stringify(productType);
  const loadedProductTypeKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (query.data) loadedProductTypeKey.current = productTypeKey;
  }, [query.data, productTypeKey]);

  const reusablePreviousData =
    loadedProductTypeKey.current === productTypeKey
      ? query.previousData
      : undefined;

  const preferences = React.useMemo(
    () =>
      getDataOrNull((query.data ?? reusablePreviousData)?.pimProductPreferences)
        ?.preferences ?? null,
    [query.data, reusablePreviousData]
  );

  const preferencesByArticle = React.useMemo(
    () => (preferences ? indexPreferencesByArticle(preferences) : null),
    [preferences]
  );

  const [addProductPreferences, addState] = useAddProductPreferencesMutation({
    client,
    refetchQueries: [namedOperations.Query.ProductPreferences],
    awaitRefetchQueries: true,
    update: evictRankedProductSearches,
  });
  const [deleteProductPreferences, deleteState] =
    useDeleteProductPreferencesMutation({
      client,
      refetchQueries: [namedOperations.Query.ProductPreferences],
      awaitRefetchQueries: true,
      update: evictRankedProductSearches,
    });

  const getPreferredScopes = React.useCallback(
    (identifier: { supplierId: string; articleNumber: string }) =>
      preferencesByArticle?.get(articleNumberIdentifierKey(identifier)) ??
      NO_PREFERENCE,
    [preferencesByArticle]
  );

  // Memoized: callers put this object in dependency arrays, and a fresh literal per render
  // remounts the controls built from it — tearing down an open menu mid-interaction.
  return React.useMemo(
    () => ({
      preferredStateKnown: preferencesByArticle !== null,
      // A failed read leaves the state unknown, never empty; the screen reports it instead.
      error: query.error ?? null,
      preferences: preferences ?? EMPTY_PREFERENCES,
      getPreferredScopes,
      addProductPreferences,
      deleteProductPreferences,
      writing: addState.loading || deleteState.loading,
    }),
    [
      preferencesByArticle,
      query.error,
      preferences,
      getPreferredScopes,
      addProductPreferences,
      deleteProductPreferences,
      addState.loading,
      deleteState.loading,
    ]
  );
}

const EMPTY_PREFERENCES: readonly ProductPreferenceFragment[] = [];

export type UseProductPreferences = ReturnType<typeof useProductPreferences>;
export type { ProductPreferenceScopes };
